import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase, isConfigured } from '../lib/supabase.js'
import Seo from '../components/Seo.jsx'

/*
 * Admin email log — recent welcome + dose-reminder emails, plus summary counts.
 * Reads via the admin_email_log() security-definer RPC (auth.users / welcome_log /
 * other users' events are all protected). See supabase/admin-email-log.sql.
 */
const when = (ts) => {
  if (!ts) return '—'
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function AdminEmailLog() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return }
    supabase.rpc('admin_email_log').then(({ data, error }) => {
      if (error) setErr(error.message)
      else setData(data)
      setLoading(false)
    })
  }, [])

  const s = data?.summary || {}
  const welcomes = data?.welcomes || []
  const reminders = data?.reminders || []

  return (
    <main className="page">
      <Seo title="Email Log | CalculateMyPeptides" noindex />
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Admin</Link>
        <span className="pill blue" style={{ marginBottom: 8 }}>Admin</span>
        <h1 className="section-title">Email Log</h1>
        <p className="section-sub" style={{ maxWidth: '60ch' }}>
          Welcome and dose-reminder emails your crons have actually sent — straight from the database.
        </p>

        {loading && <div className="card empty"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading…</p></div>}

        {!loading && !isConfigured && <div className="config-notice"><strong>Backend not connected.</strong></div>}

        {!loading && err && (
          <div className="config-notice">
            <strong>Couldn’t load the email log.</strong>
            <p style={{ marginBottom: 0 }}>
              {/not authorized/i.test(err)
                ? 'You must be signed in as an admin.'
                : <>Run <code>supabase/admin-email-log.sql</code> in Supabase, then reload. ({err})</>}
            </p>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="stat-tiles">
              <div className="card stat-tile">
                <span className="stat-num">{s.reminders_total ?? 0}</span>
                <span className="stat-lbl">Reminders sent</span>
                <span className="stat-delta flat">{s.reminders_7d ?? 0} in the last 7 days</span>
              </div>
              <div className="card stat-tile">
                <span className="stat-num">{s.welcomes_total ?? 0}</span>
                <span className="stat-lbl">Welcome emails</span>
                <span className="stat-delta flat">one per new signup</span>
              </div>
              <div className="card stat-tile">
                <span className="stat-num">{s.reminders_on ?? 0}</span>
                <span className="stat-lbl">Reminders turned on</span>
                <span className="stat-delta flat">users opted in</span>
              </div>
            </div>

            <section className="pep-section">
              <h2 className="pep-h2">Dose reminders <span className="muted small">(last 50)</span></h2>
              {reminders.length === 0 ? (
                <p className="muted small">No dose reminders sent yet — they fire the day before a scheduled dose, for users who’ve opted in.</p>
              ) : (
                <div className="admin-list">
                  {reminders.map((r, i) => (
                    <div className="card admin-row" key={i}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                        <div className="muted small">{r.dose || 'Dose'} · for {r.dose_date}</div>
                      </div>
                      <span className="muted small" style={{ whiteSpace: 'nowrap' }}>{when(r.sent_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="pep-section">
              <h2 className="pep-h2">Welcome emails <span className="muted small">(last 50)</span></h2>
              {welcomes.length === 0 ? (
                <p className="muted small">No welcome emails logged yet.</p>
              ) : (
                <div className="admin-list">
                  {welcomes.map((w, i) => (
                    <div className="card admin-row" key={i}>
                      <div style={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.email}</div>
                      <span className="muted small" style={{ whiteSpace: 'nowrap' }}>{when(w.sent_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <p className="muted small" style={{ marginTop: 8 }}>
              Shows the 50 most recent of each. Reminders come from <code>events.reminded_at</code>; welcomes from <code>welcome_log</code>.
              For the full raw log, query those tables in Supabase.
            </p>
          </>
        )}
      </div>
    </main>
  )
}
